/**
 * @hook useVibeMode
 * @description Manages voice-first "Vibe Mode" state for the Neeli AI assistant
 * 
 * Vibe Mode enables hands-free, voice-controlled interaction with the platform.
 * When active, users can speak commands to manage their schedule, tasks, notes, and more.
 * 
 * This hook wraps the VibeContext and provides convenient methods for:
 * - Entering/exiting voice mode
 * - Toggling voice mode
 * - Switching back to dashboard (exits voice mode)
 * 
 * @returns {Object} Vibe mode controls and state
 * @returns {boolean} returns.isVibeMode - Whether vibe mode is currently active
 * @returns {() => void} returns.toggleVibeMode - Toggle vibe mode on/off
 * @returns {() => void} returns.enterVoiceMode - Explicitly enter vibe mode
 * @returns {() => void} returns.exitVoiceMode - Explicitly exit vibe mode
 * @returns {() => void} returns.switchToDashboard - Exit vibe mode and return to dashboard view
 * 
 * @example
 * // Basic toggle button
 * const { isVibeMode, toggleVibeMode } = useVibeMode();
 * 
 * <Button onClick={toggleVibeMode}>
 *   {isVibeMode ? 'Exit Voice' : 'Start Voice'}
 * </Button>
 * 
 * @example
 * // Conditional rendering based on mode
 * const { isVibeMode, exitVoiceMode } = useVibeMode();
 * 
 * if (isVibeMode) {
 *   return <VoiceInterface onClose={exitVoiceMode} />;
 * }
 * return <StandardDashboard />;
 * 
 * @example
 * // Navigation with mode switching
 * const { switchToDashboard } = useVibeMode();
 * 
 * const handleBackToDashboard = () => {
 *   switchToDashboard();
 *   navigate('/dashboard');
 * };
 * 
 * @see VibeContext - The underlying context provider
 * @see UnifiedVoiceOverlay - The voice UI component that uses this hook
 * @see useKoojoAgent - The AI agent that processes voice commands
 * @since 1.0.0
 */

import { useVibe } from '@/contexts/VibeContext';
import { useCallback } from 'react';

/** Voice interaction mode type (for future expansion) */
export type VoiceMode = 'push-to-talk' | 'continuous';

export function useVibeMode() {
  const { isVibeMode, enableVibeMode, disableVibeMode, toggleVibeMode } = useVibe();

  /**
   * @function enterVoiceMode
   * @description Explicitly activates vibe/voice mode
   * Memoized to prevent unnecessary re-renders
   */
  const enterVoiceMode = useCallback(() => {
    enableVibeMode();
  }, [enableVibeMode]);

  /**
   * @function exitVoiceMode
   * @description Explicitly deactivates vibe/voice mode
   * Memoized to prevent unnecessary re-renders
   */
  const exitVoiceMode = useCallback(() => {
    disableVibeMode();
  }, [disableVibeMode]);

  /**
   * @function switchToDashboard
   * @description Exits voice mode and prepares for dashboard view
   * Useful when navigating away from voice-centric screens
   */
  const switchToDashboard = useCallback(() => {
    disableVibeMode();
  }, [disableVibeMode]);

  return {
    isVibeMode,
    toggleVibeMode,
    enterVoiceMode,
    exitVoiceMode,
    switchToDashboard,
  };
}
